/**
 * Tier A storefront subdomains: `atlas.eurocollis.com` serves the storefront
 * for alias `atlas`. The slug is derived from the Host header (client-side,
 * from window.location) so no per-courier Vercel config is needed — a single
 * wildcard domain + DNS CNAME covers every courier.
 *
 * Canonical-host rule: when a subdomain serves the page, it IS the canonical
 * host for that storefront; /:slug on the apex should 301 to it (handled by
 * the canonical/redirect layer, not here).
 */

/** Subdomains that must never be treated as a courier slug. */
const RESERVED_SUBDOMAINS = new Set([
  "www",
  "api",
  "app",
  "admin",
  "mail",
  "cdn",
  "static",
  "staging",
  "support",
  "status",
  "blog",
  "docs",
]);

/** Root hosts the platform lives on (subdomains of these are storefront slugs). */
const PLATFORM_ROOTS = ["eurocollis.com"];

const SLUG_RE = /^[a-z0-9][a-z0-9-]{1,47}$/;

/**
 * Returns the courier slug when the current host is a storefront subdomain
 * (e.g. `atlas` on `atlas.eurocollis.com`), or null on any other host —
 * including localhost, preview hosts, and reserved subdomains.
 */
export function getStorefrontSubdomain(hostname?: string): string | null {
  const host = (hostname ?? window.location.hostname).toLowerCase();
  for (const root of PLATFORM_ROOTS) {
    if (host === root) return null;
    if (!host.endsWith(`.${root}`)) continue;
    const sub = host.slice(0, -(root.length + 1));
    // Only single-label subdomains are slugs (no nested a.b.eurocollis.com).
    if (sub.includes(".")) return null;
    if (RESERVED_SUBDOMAINS.has(sub)) return null;
    if (!SLUG_RE.test(sub)) return null;
    return sub;
  }
  return null;
}

/** Hosts that are the platform itself (never a courier custom domain). */
const NON_CUSTOM_HOST_RE =
  /(^|\.)eurocollis\.com$|(^|\.)lovable\.app$|(^|\.)lovableproject\.com$|(^|\.)vercel\.app$|^localhost$|^127\.0\.0\.1$|^\[?::1\]?$/i;

/**
 * True when the current host belongs to the platform (apex, subdomain,
 * preview or local). Anything else is a courier-owned custom domain
 * (amine.com) pointed at us — the slug then comes from a host lookup.
 */
export function isPlatformHost(hostname?: string): boolean {
  const host = (hostname ?? (typeof window !== "undefined" ? window.location.hostname : "")).toLowerCase();
  return !host || NON_CUSTOM_HOST_RE.test(host);
}

/** The courier custom domain currently being served, if any. */
export function getStorefrontCustomHost(hostname?: string): string | null {
  const host = (hostname ?? (typeof window !== "undefined" ? window.location.hostname : "")).toLowerCase();
  if (!host || isPlatformHost(host)) return null;
  return host;
}

export interface StorefrontHostConfig {
  /** Tier A: slug.eurocollis.com is live for this courier. */
  subdomainEnabled?: boolean | null;
  /** Tier B: verified courier-owned domain (apex form, no scheme). */
  customDomain?: string | null;
}

/**
 * Canonical origin (+ optional path prefix) for a storefront.
 *
 * Priority, highest first — this is the single place the rule lives, so
 * turning subdomains or custom domains on later needs no other change:
 *   1. verified custom domain   → https://amine.com
 *   2. Tier A subdomain enabled → https://amine.eurocollis.com
 *   3. path URL on the apex     → https://www.eurocollis.com/amine
 */
export function storefrontBase(slug: string, cfg: StorefrontHostConfig = {}): { origin: string; prefix: string } {
  const custom = (cfg.customDomain || "").trim().toLowerCase().replace(/^https?:\/\//, "").replace(/\/$/, "");
  if (custom) return { origin: `https://${custom}`, prefix: "" };
  const servedCustom = getStorefrontCustomHost();
  if (servedCustom) return { origin: `https://${servedCustom}`, prefix: "" };
  const sub = getStorefrontSubdomain();
  if (sub) {
    const proto = typeof window !== "undefined" ? window.location.protocol : "https:";
    const host = typeof window !== "undefined" ? window.location.host : `${slug}.eurocollis.com`;
    return { origin: `${proto}//${host}`, prefix: "" };
  }
  if (cfg.subdomainEnabled) return { origin: `https://${slug}.eurocollis.com`, prefix: "" };
  return { origin: "https://www.eurocollis.com", prefix: `/${slug}` };
}

/**
 * Canonical absolute URL for a storefront page.
 * `path` is appended after the language prefix (e.g. `/route/casablanca-lyon`).
 */
export function storefrontCanonicalUrl(
  slug: string,
  langPrefix?: string,
  cfg: StorefrontHostConfig | boolean = {},
  path = "",
): string {
  const config: StorefrontHostConfig = typeof cfg === "boolean" ? { subdomainEnabled: cfg } : cfg;
  const { origin, prefix } = storefrontBase(slug, config);
  const lang = langPrefix && langPrefix !== "en" ? `/${langPrefix}` : "";
  const tail = `${lang}${prefix}${path}`;
  // Hosting runs with trailingSlash: false — only a bare host root keeps its
  // slash, every other canonical is slash-free.
  return tail ? `${origin}${tail}` : `${origin}/`;
}

